import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, Loader2, X } from "lucide-react";
import Bg from "./images/headBg.png";

// Image Modal Component
const ImageModal = ({ images, currentIndex, onClose, onNavigate }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [preloadedImages, setPreloadedImages] = useState(new Set());

  useEffect(() => {
    setIsLoaded(false);
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const imagesToPreload = [
      currentIndex,
      (currentIndex + 1) % images.length,
      currentIndex === 0 ? images.length - 1 : currentIndex - 1,
    ];

    imagesToPreload.forEach((idx) => {
      if (!preloadedImages.has(idx)) {
        const img = new Image();
        const imageUrl = images[idx]?.image_url || images[idx]?.url;
        img.src = imageUrl;
        img.onload = () => {
          setPreloadedImages((prev) => new Set([...prev, idx]));
        };
      }
    });
  }, [currentIndex, images, preloadedImages]);

  useEffect(() => {
    setIsLoaded(preloadedImages.has(currentIndex));
  }, [currentIndex, preloadedImages]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onNavigate("prev");
      if (e.key === "ArrowRight") onNavigate("next");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, onNavigate]);

  const currentImage = images[currentIndex];
  const imageUrl = currentImage?.image_url || currentImage?.url;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        background: "linear-gradient(135deg, rgba(0,0,0,0.98) 0%, rgba(15,15,25,0.98) 100%)",
        backdropFilter: "blur(10px)",
        zIndex: 9999,
      }}
      onClick={onClose}
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      </div>

      <button onClick={onClose} className="absolute top-6 right-6 z-50 group">
        <div className="relative bg-gradient-to-br from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-full p-3 shadow-2xl transition-all duration-300 transform hover:scale-110 hover:rotate-90">
          <X className="w-7 h-7 text-white" strokeWidth={2.5} />
        </div>
      </button>

      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate("prev");
          }}
          className="absolute left-6 md:left-10 top-1/2 transform -translate-y-1/2 z-50 group"
        >
          <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 hover:from-yellow-500 hover:to-yellow-600 rounded-full p-4 shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-x-1">
            <ChevronLeft className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
        </button>
      )}

      <div
        className="relative z-10 flex items-center justify-center w-full h-full px-4 md:px-20 py-20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative max-w-7xl max-h-full">
          {!isLoaded && (
            <div className="absolute inset-0 flex flex-col items-center justify-center min-h-[400px]">
              <div className="relative">
                <Loader2 className="w-16 h-16 text-yellow-500 animate-spin" strokeWidth={2} />
              </div>
              <p className="mt-4 text-white/60 text-sm font-medium">Loading image...</p>
            </div>
          )}

          <div className={`relative transition-all duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}>
            <div className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 via-blue-500/20 to-yellow-500/20 rounded-2xl blur-2xl"></div>
            <div className="relative bg-gradient-to-br from-gray-900 to-black p-2 rounded-2xl shadow-2xl">
              <img
                src={imageUrl}
                alt={currentImage?.image_type || "Gallery Image"}
                className="max-w-full max-h-[80vh] w-auto h-auto object-contain rounded-xl shadow-2xl"
                onLoad={() => setIsLoaded(true)}
              />
            </div>
          </div>
        </div>
      </div>

      {images.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onNavigate("next");
          }}
          className="absolute right-6 md:right-10 top-1/2 transform -translate-y-1/2 z-50 group"
        >
          <div className="relative bg-gradient-to-br from-gray-800 to-gray-900 hover:from-yellow-500 hover:to-yellow-600 rounded-full p-4 shadow-2xl transition-all duration-300 transform hover:scale-110 hover:translate-x-1">
            <ChevronRight className="w-8 h-8 text-white" strokeWidth={2.5} />
          </div>
        </button>
      )}

      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-50">
        <div className="relative bg-gradient-to-r from-gray-900/90 to-black/90 backdrop-blur-md px-6 py-3 rounded-full border border-white/10 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/10 to-blue-500/10 rounded-full"></div>
          <p className="relative text-white font-bold text-lg tracking-wider">
            <span className="text-yellow-400">{currentIndex + 1}</span>
            <span className="text-white/40 mx-2">/</span>
            <span className="text-white/80">{images.length}</span>
          </p>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 via-blue-500 to-yellow-500"></div>
    </div>
  );
};

// Memoized OptimizedImage component with GPU acceleration
const OptimizedImage = React.memo(({ image, imgIdx, slideIdx, onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const imgRef = useRef(null);
  const imageUrl = image.image_url || image.url;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: "200px",
        threshold: 0.01,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={imgRef}
      onClick={onClick}
      className="aspect-[3/4] overflow-hidden rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 cursor-pointer bg-gray-100 relative"
      style={{ 
        willChange: 'transform',
        transform: 'translateZ(0)',
        backfaceVisibility: 'hidden'
      }}
    >
      {!isLoaded && isInView && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <Loader2 className="w-8 h-8 md:w-10 md:h-10 text-yellow-500 animate-spin" />
        </div>
      )}

      {isInView && (
        <img
          src={imageUrl}
          alt={image.image_type || `Gallery ${imgIdx + 1}`}
          className={`w-full h-full object-cover transition-opacity duration-300 ${
            isLoaded ? "opacity-100" : "opacity-0"
          }`}
          onLoad={() => setIsLoaded(true)}
          loading="lazy"
          decoding="async"
        />
      )}
    </div>
  );
});

OptimizedImage.displayName = 'OptimizedImage';

const Gallery = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const autoPlayRef = useRef(null);
  const transitionTimeoutRef = useRef(null);

  const images = useSelector((state) => state.appSlice.images);

  const allImages = useMemo(
    () => [
      ...images.sportEvent,
      ...images.weddingEvent,
      ...images.corporateEvent,
    ],
    [images.sportEvent, images.weddingEvent, images.corporateEvent]
  );

  const slides = useMemo(() => {
    const imagesPerSlide = 16;
    const result = [];
    for (let i = 0; i < allImages.length; i += imagesPerSlide) {
      result.push(allImages.slice(i, i + imagesPerSlide));
    }
    return result;
  }, [allImages]);

  // Batch preload next slide images with priority
  useEffect(() => {
    if (slides.length > 1) {
      const nextSlideIdx = (currentSlide + 1) % slides.length;
      
      const preloadBatch = () => {
        if (slides[nextSlideIdx]) {
          slides[nextSlideIdx].slice(0, 4).forEach((image) => {
            const img = new Image();
            img.src = image.image_url || image.url;
          });
        }
      };

      if ('requestIdleCallback' in window) {
        requestIdleCallback(preloadBatch, { timeout: 2000 });
      } else {
        setTimeout(preloadBatch, 100);
      }
    }
  }, [currentSlide, slides]);

  // Optimized autoplay with cleanup
  useEffect(() => {
    if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }

    if (isAutoPlaying && slides.length > 1 && !modalOpen) {
      autoPlayRef.current = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 4000);
    }

    return () => {
      if (autoPlayRef.current) {
        clearInterval(autoPlayRef.current);
      }
    };
  }, [isAutoPlaying, slides.length, modalOpen]);

  // Debounced slide change handler
  const handleSlideChange = useCallback((slideOrFunction) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentSlide(slideOrFunction);
    
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
    }
    
    transitionTimeoutRef.current = setTimeout(() => {
      setIsTransitioning(false);
    }, 600);
  }, [isTransitioning]);

  const nextSlide = useCallback(() => {
    handleSlideChange((prev) => (prev + 1) % slides.length);
  }, [handleSlideChange, slides.length]);

  const prevSlide = useCallback(() => {
    handleSlideChange((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  }, [handleSlideChange, slides.length]);

  const goToSlide = useCallback(
    (index) => {
      handleSlideChange(index);
    },
    [handleSlideChange]
  );

  const handleImageClick = useCallback((slideIdx, imgIdx) => {
    const globalIndex = slideIdx * 16 + imgIdx;
    setModalImageIndex(globalIndex);
    setModalOpen(true);
  }, []);

  const handleModalNavigate = useCallback(
    (direction) => {
      if (direction === "next") {
        setModalImageIndex((prev) => (prev + 1) % allImages.length);
      } else {
        setModalImageIndex((prev) =>
          prev === 0 ? allImages.length - 1 : prev - 1
        );
      }
    },
    [allImages.length]
  );

  // Memoized slide render with virtualization
  const renderSlide = useCallback(
    (slideIdx) => {
      const shouldRender =
        Math.abs(slideIdx - currentSlide) <= 1 ||
        (currentSlide === 0 && slideIdx === slides.length - 1) ||
        (currentSlide === slides.length - 1 && slideIdx === 0);

      if (!shouldRender) {
        return <div key={slideIdx} className="min-w-full px-2" />;
      }

      const slideImages = slides[slideIdx];

      return (
        <div key={slideIdx} className="min-w-full px-2">
          <div className="grid grid-cols-4 gap-2 md:gap-3 lg:gap-4">
            {slideImages.map((image, imgIdx) => (
              <OptimizedImage
                key={`${slideIdx}-${imgIdx}`}
                image={image}
                imgIdx={imgIdx}
                slideIdx={slideIdx}
                onClick={() => handleImageClick(slideIdx, imgIdx)}
              />
            ))}
          </div>
        </div>
      );
    },
    [currentSlide, slides, handleImageClick]
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="relative h-[35vh] sm:h-[40vh] md:h-[50vh] lg:h-[60vh] flex items-center justify-center overflow-hidden mb-8 md:mb-12 px-4">
        <div
          className="absolute inset-0 bg-contain bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${Bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            willChange: 'transform',
            transform: 'translateZ(0)'
          }}
        ></div>

        <div className="absolute inset-0 bg-gradient-to-b from-blue-400/60 via-blue-500/50 to-green-600/40"></div>

        <h1
          className="relative text-3xl sm:text-4xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white tracking-wider z-10 text-center px-4 max-w-full break-words"
          style={{
            textShadow: "4px 4px 8px rgba(0,0,0,0.5), 0 0 20px rgba(0,0,0,0.3)",
            fontFamily: "Arial Black, sans-serif",
            letterSpacing: "0.05em",
            wordWrap: "break-word",
            hyphens: "auto",
          }}
        >
          Happy Adventuring
        </h1>
      </div>

      {/* Carousel Section */}
      <div className="max-w-6xl mx-auto px-4 pb-16">
        {allImages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-gray-500">No images available yet.</p>
          </div>
        ) : (
          <div
            className="relative"
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
          >
            <div className="relative overflow-hidden">
              <div
                className="flex transition-transform ease-out"
                style={{
                  transform: `translateX(-${currentSlide * 100}%)`,
                  transitionDuration: '600ms',
                  willChange: 'transform',
                  backfaceVisibility: 'hidden',
                  perspective: 1000
                }}
              >
                {slides.map((_, slideIdx) => renderSlide(slideIdx))}
              </div>

              {slides.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    disabled={isTransitioning}
                    className="absolute left-[-20px] md:left-[-50px] top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 rounded-full p-2 md:p-3 shadow-lg transition-transform hover:scale-110 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ willChange: 'transform' }}
                  >
                    <ChevronLeft className="w-5 h-5 md:w-7 md:h-7 text-gray-800" />
                  </button>

                  <button
                    onClick={nextSlide}
                    disabled={isTransitioning}
                    className="absolute right-[-20px] md:right-[-50px] top-1/2 transform -translate-y-1/2 bg-white hover:bg-gray-100 rounded-full p-2 md:p-3 shadow-lg transition-transform hover:scale-110 z-10 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ willChange: 'transform' }}
                  >
                    <ChevronRight className="w-5 h-5 md:w-7 md:h-7 text-gray-800" />
                  </button>
                </>
              )}
            </div>

            {slides.length > 1 && (
              <>
                <div className="flex justify-center gap-2 md:gap-3 mt-8">
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => goToSlide(idx)}
                      disabled={isTransitioning}
                      className={`transition-all rounded-full disabled:cursor-not-allowed ${
                        currentSlide === idx
                          ? "w-8 md:w-10 h-3 md:h-4 bg-yellow-500"
                          : "w-3 md:w-4 h-3 md:h-4 bg-gray-300 hover:bg-gray-400"
                      }`}
                      aria-label={`Go to slide ${idx + 1}`}
                      style={{ willChange: 'width' }}
                    />
                  ))}
                </div>

                <div className="text-center mt-4 text-gray-600 font-medium text-sm md:text-base">
                  {currentSlide + 1} / {slides.length}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {modalOpen && (
        <ImageModal
          images={allImages}
          currentIndex={modalImageIndex}
          onClose={() => setModalOpen(false)}
          onNavigate={handleModalNavigate}
        />
      )}
    </div>
  );
};

export default Gallery;